import { useEffect, useState } from "react";
import { Copy, Download, ImagePlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AuthImg } from "@/components/studio/AuthImg";
import { api, downloadBlob, errMsg } from "@/lib/api";

const Card = ({ title, children, testId }) => (
  <div className="border border-[#2A2325]/10 bg-[#FFFDF9] p-6 space-y-4" data-testid={testId}>
    <div className="eyebrow">{title}</div>
    {children}
  </div>
);

const useBusy = () => {
  const [busy, setBusy] = useState("");
  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return [busy, run];
};

export const BundleListing = ({ b, onBundle }) => {
  const [busy, run] = useBusy();
  const l = b.listing || {};
  const write = () => run("l", async () => {
    if (l.generated_at && !window.confirm("Rewrite the listing? This replaces the subtitle and store description.")) return;
    onBundle((await api.post(`/bundles/${b.id}/listing/generate`)).data);
    toast.success("Listing written — subtitle and description updated");
  });
  return (
    <Card title="Store listing · AI" testId="bundle-listing-card">
      <Button onClick={write} disabled={busy === "l"} className="rounded-none bg-[#5E2B38] hover:bg-[#4A2029] gap-2" data-testid="bundle-listing-generate-btn">
        <Sparkles size={14} /> {busy === "l" ? "Writing…" : l.generated_at ? "Rewrite listing" : "Write listing with AI"}
      </Button>
      {l.short_description && <p className="font-body-serif italic" data-testid="bundle-listing-short">{l.short_description}</p>}
      {l.highlights?.length > 0 && (
        <ul className="text-sm list-disc pl-5 space-y-1" data-testid="bundle-listing-highlights">{l.highlights.map((h) => <li key={h}>{h}</li>)}</ul>
      )}
      {l.keywords?.length > 0 && <p className="text-xs text-[#5C5255]" data-testid="bundle-listing-keywords">Keywords: {l.keywords.join(", ")}</p>}
      <p className="text-xs text-[#5C5255]">The long description goes into Details above, and is sent to softspinepress.com with the title list and saving.</p>
    </Card>
  );
};

export const BundleArtwork = ({ b, onReload }) => {
  const [busy, run] = useBusy();
  const job = b.art_job || {};
  const draw = () => run("a", async () => {
    if (!window.confirm(`${b.art_path ? "Redraw" : "Draw"} the bundle artwork? One image, about US$0.05 of OpenRouter credit.`)) return;
    await api.post(`/bundles/${b.id}/art`);
    onReload();
  });
  return (
    <Card title="Bundle artwork · AI" testId="bundle-art-card">
      <div className="flex items-center gap-3">
        <Button onClick={draw} disabled={job.status === "running" || busy === "a"} variant="outline" className="rounded-none gap-2" data-testid="bundle-art-draw-btn">
          <ImagePlus size={14} /> {job.status === "running" ? "Drawing… (about 30s)" : b.art_path ? "Redraw artwork" : "Draw artwork"}
        </Button>
        {job.status === "failed" && <span className="text-sm text-[#9B2C2C]" data-testid="bundle-art-error">{job.error}</span>}
      </div>
      {b.art_path ? (
        <AuthImg path={`/bundles/${b.id}/art.png`} version={job.at} alt="Bundle artwork" className="w-48 aspect-square" testId="bundle-art-img" />
      ) : (
        <p className="text-sm text-[#5C5255]">One gold line illustration joining a motif from each title, in the category's colours and series style. It's used on the Instagram post and the back of the bundle PDF.</p>
      )}
    </Card>
  );
};

export const BundleInstagram = ({ b }) => {
  const [busy, run] = useBusy();
  const [ig, setIg] = useState(b.instagram || null);
  const [caption, setCaption] = useState(b.instagram?.caption || "");
  const [count, setCount] = useState(0);
  const v = `${b.art_job?.at}-${b.title}-${b.subtitle}-${b.price}-${b.saving_pct}-${b.items.map((i) => i.id + i.title + i.subtitle + i.art).join()}`;
  useEffect(() => {
    api.get(`/bundles/${b.id}/instagram/count`).then((r) => setCount(r.data.slides)).catch(() => setCount(0));
  }, [b.id, v]);
  useEffect(() => {
    setIg(b.instagram || null);
    setCaption(b.instagram?.caption || "");
  }, [b.instagram]);
  const write = () => run("c", async () => {
    const r = await api.post(`/bundles/${b.id}/instagram/caption`);
    setIg(r.data);
    setCaption(r.data.caption);
  });
  const full = `${caption}\n\n${(ig?.hashtags || []).map((t) => `#${t}`).join(" ")}`;
  return (
    <Card title="Instagram post · AI" testId="bundle-instagram-card">
      <div className="grid sm:grid-cols-[200px_1fr] gap-5">
        <AuthImg path={`/bundles/${b.id}/instagram/post.png`} version={v} alt="Instagram post" className="w-full aspect-[4/5]" testId="bundle-instagram-post-img" />
        <div className="space-y-3">
          <Button onClick={write} disabled={busy === "c"} className="rounded-none bg-[#5E2B38] hover:bg-[#4A2029] gap-2" data-testid="bundle-caption-generate-btn">
            <Sparkles size={14} /> {busy === "c" ? "Writing…" : ig ? "Rewrite caption" : "Write caption with AI"}
          </Button>
          {ig && (
            <>
              <Textarea rows={7} value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-none bg-white text-sm" data-testid="bundle-caption-input" />
              <p className="text-xs text-[#5C5255]" data-testid="bundle-hashtags">{ig.hashtags.map((t) => `#${t}`).join(" ")}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => run("s", async () => { await api.put(`/bundles/${b.id}/instagram/caption`, { caption }); toast.success("Caption saved"); })} data-testid="bundle-caption-save-btn">Save</Button>
                <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => navigator.clipboard.writeText(full).then(() => toast.success("Caption & hashtags copied"))} data-testid="bundle-caption-copy-btn"><Copy size={12} /> Copy</Button>
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => downloadBlob(`/bundles/${b.id}/instagram/post.png?download=1`, "bundle-instagram.png")} data-testid="bundle-post-download-btn"><Download size={12} /> Post 1080×1350</Button>
            <Button size="sm" variant="outline" className="rounded-none gap-1" disabled={busy === "z"} onClick={() => run("z", () => downloadBlob(`/bundles/${b.id}/instagram/carousel.zip`, "bundle-carousel.zip"))} data-testid="bundle-carousel-download-btn"><Download size={12} /> {busy === "z" ? "Preparing…" : "Carousel ZIP"}</Button>
          </div>
        </div>
      </div>
      <div className="border-t border-[#2A2325]/10 pt-4" data-testid="bundle-carousel">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow" data-testid="bundle-carousel-count">Carousel · {count} slides</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2" data-testid="bundle-carousel-slides">
          {Array.from({ length: count }).map((_, i) => (
            <AuthImg key={i} path={`/bundles/${b.id}/instagram/slide/${i}.png`} version={v} alt={`Slide ${i + 1}`} className="w-full aspect-[4/5] object-cover" testId={`bundle-carousel-slide-${i}`} />
          ))}
        </div>
        <p className="text-xs text-[#5C5255] mt-2">Post the slides in order: the bundle cover, one slide per title, then "In this bundle" with the saving. The ZIP includes caption.txt.</p>
      </div>
    </Card>
  );
};
